/**
 * Switch — Drawer highlight CTA behavior
 *
 * Makes the mobile drawer-highlight button (`snippets/switch-drawer-highlight.liquid`)
 * close the header menu drawer when clicked, then — for a same-page anchor link —
 * scroll to the target AFTER the drawer has finished closing, so you land at the
 * anchor with the drawer already collapsed.
 *
 * Stack note (FRONTEND-STACK §29 / CLAUDE.md §4.5): the menu drawer is a
 * Horizon-component-owned element (`<header-drawer>`). We never hand-manage its
 * open state — we call its OWN public `close()` method (animated, handles focus
 * trap + scroll-lock release), then react to the real close (the `open` attribute
 * being removed) before scrolling. Pure vanilla JS (logic/integration layer);
 * the scroll uses native smooth-scroll (standard anchor behavior), not GSAP, and
 * honors prefers-reduced-motion.
 *
 * Idempotent + delegated, so it survives the drawer being re-rendered by the
 * Section Rendering API and works no matter which drawer slot the button is in.
 *
 * Registered in the CAPTURE phase so it runs before the global anchor-scroll
 * handler (switch-anchor-scroll.js / AST-010, bubble phase). Calling
 * preventDefault() here makes that handler stand down on its own (it bails when
 * `event.defaultPrevented`), so the drawer CTA gets exactly one owner — this one
 * (which must close the drawer *before* scrolling). We mirror its a11y focus.
 */
(function () {
  if (window.__switchDrawerHighlightInit) return;
  window.__switchDrawerHighlightInit = true;

  var CTA_SELECTOR = '.switch-drawer-highlight__cta';
  var DETAILS_ID = 'Details-menu-drawer-container';
  var CLOSE_FALLBACK_MS = 600;

  function resolveTarget(url) {
    if (!url.hash || url.hash === '#') return null;
    var id;
    try {
      id = decodeURIComponent(url.hash.slice(1));
    } catch (e) {
      id = url.hash.slice(1);
    }
    if (!id) return null;
    return (
      document.getElementById(id) ||
      document.querySelector('a[name="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]')
    );
  }

  // Close the menu drawer via Horizon's native API, then invoke `onClosed`
  // once the drawer is actually closed (or after a safety fallback).
  function closeDrawer(link, onClosed) {
    var details = link.closest('#' + DETAILS_ID);
    if (!details || !details.hasAttribute('open')) {
      if (onClosed) onClosed();
      return;
    }

    if (onClosed) {
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        if (observer) observer.disconnect();
        onClosed();
      };
      var observer = new MutationObserver(function () {
        if (!details.hasAttribute('open')) finish();
      });
      observer.observe(details, { attributes: true, attributeFilter: ['open'] });
      window.setTimeout(finish, CLOSE_FALLBACK_MS);
    }

    var drawer = link.closest('header-drawer');
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    } else {
      // Fallbacks if the custom element isn't upgraded yet.
      var btn = details.querySelector('.menu-drawer__close-button');
      if (btn) btn.click();
      else details.removeAttribute('open');
    }
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest(CTA_SELECTOR);
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href || href === '#') return;

    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return;
    }

    var samePage =
      url.pathname === window.location.pathname &&
      url.search === window.location.search &&
      !!url.hash;
    var target = samePage ? resolveTarget(url) : null;

    if (samePage && target) {
      // In-page anchor: collapse the drawer first, then jump to the target.
      event.preventDefault();
      var reduce =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      closeDrawer(link, function () {
        if (history.replaceState) {
          try {
            history.replaceState(null, '', url.hash);
          } catch (e) {
            /* no-op */
          }
        }
        // Next frame so the scroll-lock release / layout settles before scrolling.
        requestAnimationFrame(function () {
          target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

          // a11y: move focus to the target so keyboard + screen readers follow
          // the jump (mirrors switch-anchor-scroll.js). Anchor blocks are empty
          // divs, so make it focusable just for this, then clean up on blur.
          var hadTabindex = target.hasAttribute('tabindex');
          if (!hadTabindex) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
          if (!hadTabindex) {
            target.addEventListener(
              'blur',
              function cleanup() {
                target.removeAttribute('tabindex');
                target.removeEventListener('blur', cleanup);
              },
              { once: true }
            );
          }
        });
      });
    } else {
      // Normal or cross-page link: collapse the drawer for instant feedback and
      // let the browser navigate (the new page loads with the drawer closed).
      closeDrawer(link);
    }
  }, true);
})();
